import { useCallback, useState } from "react";
import { postToApi, requestApi } from "utils/api/postToApi";
import { captureMessage } from "@sentry/react";
import { updateNonSaveableOptions } from "stores/optionsStore/actions";
import { useOptions } from "stores/optionsStore/OptionsContext";
import { useAuth } from "./useAuth";

const isSuccessOrNotFound = (status: number) =>
  (status >= 200 && status < 300) || status === 404;
export const UPLOAD_FILE_SIZE_LIMIT = 10; // MB

export class ImageError extends Error {
  link: string;

  constructor(link: string) {
    super();

    this.link = link;
  }
}

export class LinkError extends Error {}

/** The image belongs to another user (or doesn't exist anymore), so it can't be deleted */
export class ImageNotOwnedError extends Error {}

export type ImageTarget = "track" | "waypoint";

export interface StorageSize {
  cantUpload: boolean;
  percentage: number;
  storageTotal: number;
  storageUsed: number;
  uploadLimit: number;
}

export const useImageUpload = () => {
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();
  const {
    state: { language },
    dispatch: dispatchOptions,
  } = useOptions();

  const upload = useCallback(
    async (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
        if (!user) {
          reject(new Error("Not signed in"));
          return;
        }

        setUploading(true);

        const reader = new FileReader();

        reader.onload = async (e) => {
          try {
            const contents = e.target?.result as ArrayBuffer | null | undefined;
            if (!contents) throw new Error("File empty");

            const uint8Array = new Uint8Array(contents);
            const img = window.btoa(
              uint8Array.reduce(
                (acc, current) => acc + String.fromCharCode(current),
                ""
              )
            );

            // A 404 has no link either, it's a failed upload below
            const data = await postToApi<{ link?: string }>(
              "upload-image",
              language,
              { img, type: file.type },
              { validateStatus: isSuccessOrNotFound }
            );

            if (data?.link) {
              setUploading(false);
              resolve(data.link);
              return;
            }

            throw new Error("No data returned from the server");
          } catch (error) {
            setUploading(false);
            reject(error instanceof Error ? error : new Error("Upload error"));
          }
        };

        // Without it, a file that can't be read (e.g. removed meanwhile) would leave the upload running forever
        reader.onerror = () => {
          setUploading(false);
          reject(reader.error ?? new Error("The file can't be read"));
        };

        reader.readAsArrayBuffer(file);
      }),
    [language, user]
  );

  const remove = useCallback(
    async (imgName: string): Promise<void> => {
      const response = await requestApi(
        "delete-image",
        language,
        { imgName },
        {
          validateStatus: (status) =>
            isSuccessOrNotFound(status) || status === 403,
        }
      );

      // 403 and 404 are accepted as responses above (validateStatus): they mean the image isn't the user's
      if (response?.status === 403 || response?.status === 404) {
        throw new ImageNotOwnedError();
      }
    },
    [language]
  );

  const getStorageSize = useCallback(async (): Promise<StorageSize> => {
    // A 404 has no data, it's incomplete below
    const data = await postToApi<{
      storageTotal?: string;
      storageUsed?: string;
    }>(
      "get-storage-info",
      language,
      {},
      { validateStatus: isSuccessOrNotFound }
    );

    if (!data?.storageTotal || !data?.storageUsed) {
      throw new Error("Received incomplete data!");
    }

    const storageTotal = Number(data.storageTotal);
    const storageUsed = Number(data.storageUsed);

    const storageDiff = storageTotal - storageUsed;
    const cantUpload = storageUsed >= storageTotal;
    const percentage = (100 / storageTotal) * storageUsed;
    const uploadLimit =
      storageDiff < UPLOAD_FILE_SIZE_LIMIT
        ? storageDiff < 0
          ? 0
          : storageDiff
        : UPLOAD_FILE_SIZE_LIMIT;

    return {
      cantUpload,
      percentage,
      storageTotal,
      storageUsed,
      uploadLimit,
    };
  }, [language]);

  /**
   * Loads the used and free storage again (e.g. after images were uploaded or deleted) into the options.
   * Never throws: the old values stay shown and the failure is reported.
   */
  const updateStorageSize = useCallback(async () => {
    try {
      const storageSize = await getStorageSize();
      dispatchOptions(updateNonSaveableOptions({ storageSize }));
    } catch (error) {
      captureMessage(
        `Trackbook: Errors when getting storage size: ${(error as Error)?.message}`
      );
    }
  }, [dispatchOptions, getStorageSize]);

  return {
    getStorageSize,
    updateStorageSize,
    remove,
    upload,
    uploading,
    setUploading,
  };
};
